import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import {
  Box,
  Button,
  Typography,
  Alert,
  Autocomplete,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import dayjs from 'dayjs';
import { userTimeZone, timeZonesList } from '../../components/helper/timeZone';
import UIWrapper from '../../components/UIWrapper';
import AcsUniqueInvCode from '../AcsUniqueInvCode';
import { mapManifestActions } from '../../Store/map-manifest';
import { snackbarActions } from '../../Store/snackbar';
import AccService from '../../services/AccService';
import AcsService from '../../services/AcsService';
import BiService from '../../services/BiService';
import ManagementService from '../../services/ManagementService';
const carrierTypes = [
  { value: 'BIOCAIR', label: 'BIOCAIR' },
  { value: 'DHL', label: 'DHL' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'FedEx Ground', label: 'FedEx Ground' },
  { value: 'Hand Delivered', label: 'Hand Delivered' },
  { value: 'Marken', label: 'Marken' },
  { value: 'Priority Express', label: 'Priority Express' },
  { value: 'Quickstat', label: 'Quickstat' },
  { value: 'TNT', label: 'TNT' },
  { value: 'UPS', label: 'UPS' },
  { value: 'USA Courier', label: 'USA Courier' },
  { value: 'USPS', label: 'USPS' },
  { value: 'VIP Cargo', label: 'VIP Cargo' },
  { value: 'World Courrier', label: 'World Courrier' },
];
const ManifestUploadNew = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isManifestNew, setIsManifestNew] = useState(false);
  const [fileFormat, setFileFormat] = useState('csv');
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [mappingOptions, setMappingOptions] = useState([]);
  const [clientState, setClientState] = useState(null);
  const [projectState, setProjectState] = useState(null);
  const [mappingState, setMappingState] = useState(null);
  const [currentStep, setCurrentStep] = useState('upload');
  const [editedData, setEditedData] = useState([]);
  const [displayColumns, setDisplayColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [preventFutureDate, setPreventFutureDate] = useState(true);
  const [conflictMessage, setConflictMessage] = useState('');
  const [exportData, setExportData] = useState([]);
  const [mappingProfile, setMappingProfile] = useState(null);
  const [showUnique, setShowUnique] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [showLimsResponseModal, setShowLimsResponseModal] = useState(false);
  const [limsResponseData, setLimsResponseData] = useState([]);
  const [limsPageLarge, setLimsPageLarge] = useState(0);
  const [limsRowsPerPageLarge, setLimsRowsPerPageLarge] = useState(10);
  const [limsFormData, setLimsFormData] = useState({}); 
  const fileInputRef = useRef(null);
  const tableBarCodeData = useRef(null);
  const csvLinkRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const rowsPerPage = 100;
 
  useEffect(() => {
    AccService.getAllClients('')
      .then((resp) => setClientOptions(resp.data || []))
      .catch((err) => console.error('Error loading clients:', err));
  }, []);
 
  useEffect(() => {
    if (!clientState) return;
    const timeout = setTimeout(() => {
      AccService.getClientProjects(clientState, projectState)
        .then((resp) => setProjectOptions(resp.data || []))
        .catch((err) => console.error('Error loading projects:', err));
    }, 500);
    return () => clearTimeout(timeout);
  }, [clientState, projectState]);
 
  useEffect(() => {
    if (!clientState) return;
    const timeout = setTimeout(() => {
      AccService.getManifestProfiles(clientState)
        .then((resp) => setMappingOptions(resp.data || []))
        .catch((err) => console.error('Error loading profiles:', err));
    }, 500);
    return () => clearTimeout(timeout);
  }, [clientState]);
 
  const handleFileSelect = (files) => {
    if (!files?.length) return;
    const file = files[0];
    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
   
    if (!isCSV && !isExcel) {
      setError('Only CSV and Excel (XLSX, XLS) files are supported');
      return;
    }
    setSelectedFile(file);
    setIsManifestNew(isExcel);
    setFileFormat(isCSV ? 'csv' : 'excel');
    setError(null);
  };
 
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }
    if (!clientState || !projectState) {
      setError('Please select Client and Project');
      return;
    }
    if (!mappingState) {
      setError('Please select a Mapping Profile');
      return;
    } 
    setLoading(true);
    setError(null);
 
    try {
      const formData = new FormData();
      formData.append('file_obj', selectedFile);
      formData.append('is_manifest_new', isManifestNew);

      const apiData = await BiService.previewOrigNew(formData);
      const apiColumns = apiData.length > 0 ? Object.keys(apiData[0]) : [];     
      if (apiData.length === 0) {
        throw new Error('File is empty or no data returned from API');
      }
 
      setEditedData(apiData);
      setDisplayColumns(apiColumns);     
      dispatch(mapManifestActions.setUploadedData(apiData));
      dispatch(mapManifestActions.setColumns(apiColumns));
      dispatch(mapManifestActions.setClientState(clientState));
      dispatch(mapManifestActions.setProjectState(projectState));
      dispatch(mapManifestActions.setMappingState(mappingState || null));
 
      if (mappingState) {
        AccService.getManifestProfiles(clientState).then((resp) => {
          const profile = resp.data.find(p => p.name === mappingState);
          setMappingProfile(profile);
          setTimeout(() => preloadBarcodeData(selectedFile, apiData, profile), 100);
        }).catch((err) => console.error('Error loading mapping profile:', err));
      } 
      setLoading(false);
      setCurrentStep('review');
      setCurrentPage(1);
    } catch (err) {
      let errorMessage = 'Error processing file. Please try again.';     
      if (err.message === 'Network Error' || err.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. The file is taking too long to process. Please try with a smaller file or contact support.';
      } else if (err.response?.status === 504) {
        errorMessage = 'Server processing time exceeded (504). Your file may have been successfully ingested. Please refresh to check if your data appears. If still not visible after 2 minutes, contact support.';
      } else if (err.response?.status === 413) {
        errorMessage = 'File too large. Please upload a smaller file.';
      } else if (err.response?.status === 400) {
        errorMessage = `File format error: ${err.response?.data?.detail || 'Invalid file format'}`;
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to upload files.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }     
      setError(errorMessage);
      setLoading(false);
    }
  };
 
  const preloadBarcodeData = async (file, data, profile) => {
    try {
      const csvContent = [
        Object.keys(data[0]).join(','),
        ...data.map(row =>
          Object.keys(data[0]).map(col => {
            const val = row[col];
            if (val === null || val === undefined) return '';
            const str = String(val);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          }).join(',')
        )
      ].join('\n');
 
      const csvFile = new File([csvContent], 'manifest.csv', { type: 'text/csv' }); 
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('client', clientState);
      formData.append('project', projectState);
      if (mappingState) formData.append('profile', mappingState);
      if (profile) formData.append('mapping', JSON.stringify(profile.mapping));
 
      tableBarCodeData.current = (await AcsService.getTableBarCodeData(formData)).data;
    } catch (err) {
      console.log('Barcode preload skipped:', err.message);
    }
  };
 
  const generateCSVContent = (data, columns) => {
    const headers = columns.join(',');
    const dataRows = data.map((row) =>
      columns.map(col => {
        const val = row[col];
        if (val == null) return '';
        const str = String(val);
        return (str.includes(',') || str.includes('"') || str.includes('\n'))
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    );
    return [headers, ...dataRows].join('\n');
  };
 
  const handleCellChange = (rowIndex, columnName, value) => {
    setEditedData(prev => {
      const newData = [...prev];
      newData[rowIndex] = { ...newData[rowIndex], [columnName]: value };
      return newData;
    });
 
  };
 
  const handleCheckDuplicates = async () => {    
      setLoading(true);
      try {
        const csvContent = generateCSVContent(editedData, displayColumns);
        const csvFile = new File([csvContent], 'manifest.csv', { type: 'text/csv' });
        const formData = new FormData();
        formData.append('file', csvFile);
        formData.append('client', clientState);
        formData.append('project', projectState);
        if (mappingState) formData.append('profile', mappingState);
        if (mappingProfile) {
        formData.append('mapping', JSON.stringify(mappingProfile.mapping));
        formData.append('add', JSON.stringify(mappingProfile.add || {}));
        formData.append('drop', JSON.stringify(mappingProfile.drop || []));
        formData.append('order', JSON.stringify(mappingProfile.order || []));
        formData.append('rules', JSON.stringify(mappingProfile.rules || {}));
      }
 
        tableBarCodeData.current = (await AcsService.getTableBarCodeData(formData)).data;
      } catch (err) {
        console.error('Duplicate check error:', err.message);
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: err?.response?.data?.detail || 'Error checking duplicates',
          })
        );
        setLoading(false);
        return;
      }
      setLoading(false);     
    mergeEditedDataIntoBarCodeData();
    setShowUnique(true);
  };
 
  const mergeEditedDataIntoBarCodeData = () => {
    if (!tableBarCodeData.current?.data) return;
 
    const inventoryCodeCol = displayColumns.find(col =>
      col.toLowerCase().includes('inventory') && col.toLowerCase().includes('code')
    );
 
    if (!inventoryCodeCol) return;
 
    tableBarCodeData.current.data = tableBarCodeData.current.data.map((barcodeItem, idx) => {
      const edited = editedData[idx]?.[inventoryCodeCol]?.toString().trim();
      if (edited && edited !== barcodeItem.inventory_code) {
        return { ...barcodeItem, inventory_code: edited };
      }
      return barcodeItem;
    });
  };
 
  const handleUniqueCodesCallback = async (data) => {
    setShowUnique(false);
    tableBarCodeData.current = data;
    if (currentFlow === 'lims') {
      await handleLimsSubmit();
    } else {
      setShowConfirmationDialog(true);
    }
  };
 
  const handleCloseUnique = () => {
    setShowUnique(false);
  };
 
  const handleConfirmationShow = async () => {
    try {
      if (!editedData?.length) throw new Error('No data to ingest');
 
      const csvContent = generateCSVContent(editedData, displayColumns);
      const editedFile = new File([csvContent], 'manifest.csv', { type: 'text/csv' });
 
      window.__manifestEditedFile = editedFile;
      window.__manifestEditedCSVContent = csvContent;
 
      await handleCheckDuplicates();
    } catch (err) {
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: err.message || 'Error preparing manifest',
        })
      );
    }
  };
 
  const handleConfirmationConfirm = async () => {
    setShowConfirmationDialog(false);
    setCurrentFlow(null);
    await handleSubmit(false);
  };
 
  const handleSubmit = async (isSendToLims = false) => {
    setSubmitting(true);
 
    try {
      const editedFile = window.__manifestEditedFile;
      if (!editedFile) {
        throw new Error('Edited file not found');
      }
 
      let barcodeMapping = {};
      if (tableBarCodeData.current?.data && Array.isArray(tableBarCodeData.current.data)) {
        barcodeMapping = tableBarCodeData.current.data.reduce((accum, curr) => {
          accum[curr.received_barcode] = curr.inventory_code;
          return accum;
        }, {});
      }
 
      const resp = await BiService.ingestManifestFile(
        editedFile,
        clientState,
        projectState,
        barcodeMapping,
        mappingState || undefined,
        preventFutureDate,
        isManifestNew,
        isSendToLims
      );
 
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: resp.data,
        })
      );
 
      delete window.__manifestEditedFile;
      delete window.__manifestEditedCSVContent;
 
      resetFormState();
    } catch (error) {
      setSubmitting(false);
 
      if (error.response?.status === 406) {
        setConflictMessage(error.response.data.detail);
        setShowConflictDialog(true);
        setPreventFutureDate(false);
        if (isSendToLims) setCurrentFlow('lims_conflict');
      } else {
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: error?.response?.data?.detail || 'Error submitting manifest',
          })
        );
      }
    }
  };
 
  const handleConflictConfirm = async () => {
    setShowConflictDialog(false);
    setSubmitting(true);
 
    try {
      const csvContent = generateCSVContent(editedData, displayColumns);
      const editedFile = new File([csvContent], 'manifest.csv', { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', editedFile);
      formData.append('client', clientState);
      formData.append('project', projectState);
      if (mappingState) {
        formData.append('profile', mappingState);
      }
      formData.append('is_manifest_new', isManifestNew);
      if (mappingProfile) formData.append('mapping', JSON.stringify(mappingProfile.mapping));
      const barcodes = tableBarCodeData.current?.data?.reduce((acc, curr) => ({
        ...acc,
        [curr.received_barcode]: curr.inventory_code
      }), {}) || {};
      formData.append('barcode_received', JSON.stringify(barcodes));
      formData.append('prevent_future_collection_dates', String(preventFutureDate));
      formData.append('is_send_to_lims', String(currentFlow === 'lims_conflict'));
 
      const resp = await BiService.ingestManifestFileFormData(formData); 
      if (currentFlow === 'lims_conflict' && resp.data) {
        const dataArray = Array.isArray(resp.data) ? resp.data : [resp.data];
        setLimsResponseData(dataArray);
        const formDataInit = {};
        dataArray.forEach((_, idx) => {
          formDataInit[idx] = {
            carrier: '',
            date_received: dayjs().format('YYYY-MM-DD'),
            time_received: dayjs().format('HH:mm'),
            dt_received_timezone: userTimeZone,
          };
        });
        setLimsFormData(formDataInit);
        setSubmitting(false);
        setShowLimsResponseModal(true);
        return;
      }
 
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: resp.data,
        })
      );
 
      resetFormState();
    } catch (error) {
      setSubmitting(false);
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: error?.response?.data?.detail || 'Error submitting manifest',
        })
      );
    }
  };
 
  const onExport = async () => {
    if (!mappingProfile) {
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Please select a mapping profile',
        })
      );
      return;
    }
 
    try {
      const csvContent = generateCSVContent(editedData, displayColumns);
      const csvFile = new File([csvContent], 'manifest.csv', { type: 'text/csv' });     
      const resp = await BiService.mapManifestExportLegacy(csvFile, {
        add: mappingProfile.add,
        drop: mappingProfile.drop,
        order: mappingProfile.order,
        rules: mappingProfile.rules,
        mapping: mappingProfile.mapping,
      }, fileFormat === 'excel');
 
      if (fileFormat === 'excel') {        
        const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `manifest_${clientState}_${projectState}_${new Date().getTime()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {        
        setExportData(resp.data);
        setTimeout(() => {
          if (csvLinkRef.current) {
            csvLinkRef.current.link.download = `manifest_${clientState}_${projectState}_${new Date().getTime()}.csv`;
            csvLinkRef.current.link.click();
          }
        }, 100);
      }
 
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: 'Manifest data exported successfully',
        })
      );
    } catch (error) {
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: error?.response?.data?.detail || 'Error exporting data',
        })
      );
    }
  };
 
  const handleProceedToLims = async () => {
    try {
      if (!editedData?.length) throw new Error('No data to ingest');
 
      const csvContent = generateCSVContent(editedData, displayColumns);
      const editedFile = new File([csvContent], 'manifest.csv', { type: 'text/csv' });
      window.__manifestEditedFile = editedFile;
      window.__manifestEditedCSVContent = csvContent;
      setCurrentFlow('lims');
 
      await handleCheckDuplicates();
    } catch (err) {
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: err.message || 'Error preparing manifest for LIMS',
        })
      );
    }
  };
 
  const handleLimsSubmit = async () => {
    setSubmitting(true);
 
    try {
      const editedFile = window.__manifestEditedFile;
      if (!editedFile) {
        throw new Error('Edited file not found');
      }
 
      const barcodeMapping = tableBarCodeData.current?.data?.reduce((acc, curr) => ({
        ...acc,
        [curr.received_barcode]: curr.inventory_code
      }), {}) || {};
 
      const resp = await BiService.ingestManifestFile(
        editedFile,
        clientState,
        projectState,
        barcodeMapping,
        mappingState || undefined,
        preventFutureDate,
        true,
        true
      );
 
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: 'Manifest processed successfully. Please review tracking details.',
        })
      );
 
      delete window.__manifestEditedFile;
      delete window.__manifestEditedCSVContent;
 
      if (resp.data) {
        const dataArray = Array.isArray(resp.data) ? resp.data : [resp.data];
        setLimsResponseData(dataArray);
 
        const formDataInit = {};
        dataArray.forEach((_, idx) => {
          formDataInit[idx] = {
            carrier: '',
            date_received: dayjs().format('YYYY-MM-DD'),
            time_received: dayjs().format('HH:mm'),
            dt_received_timezone: userTimeZone,
          };
        });
        setLimsFormData(formDataInit);
      }
      setShowLimsResponseModal(true);
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
 
      if (error.response?.status === 406) {
        setConflictMessage(error.response.data.detail);
        setShowConflictDialog(true);
        setPreventFutureDate(false);
        setCurrentFlow('lims_conflict');
      } else {
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: error?.response?.data?.detail || 'Error submitting to LIMS',
          })
        );
      }
    }
  };
 
  const handleLimsFormFieldChange = (index, fieldName, value) => {
    setLimsFormData((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [fieldName]: value,
      },
    }));
  };
 
  const resetFormState = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setEditedData([]);
    setDisplayColumns([]);
    setCurrentPage(1);
    setSubmitting(false);
    setShowConflictDialog(false);
    setShowConfirmationDialog(false);
    setPreventFutureDate(true);
    setConflictMessage('');
    setClientState(null);
    setProjectState(null);
    setMappingState(null);
    setMappingProfile(null);
    setShowUnique(false);
    setError(null);
    setCurrentFlow(null);
    setShowLimsResponseModal(false);
    setLimsResponseData([]);
    setLimsPageLarge(0);
    setLimsFormData({});
    setFileFormat('csv');
    tableBarCodeData.current = null;
  }
 
  const handleSendToLimsSubmit = async () => {
    setSubmitting(true);
 
    try {
      const hasErrors = Object.values(limsFormData).some(
        (row) => !row?.carrier || !row?.date_received || !row?.time_received || !row?.dt_received_timezone
      );
 
      if (hasErrors) {
        dispatch(
          snackbarActions.showNotification({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: 'Please fill in all required fields: Carrier, Date Received, Time Received, and Timezone.',
          })
        );
        setSubmitting(false);
        return;
      }
 
      const tracking_details_data = limsResponseData.map((row, idx) => ({
        client: row.client,
        project: row.project,
        tracking_number: row.tracking_number,
        accessioning_mainfest_count: row.accessioning_mainfest_count,
        carrier: limsFormData[idx]?.carrier || '',
        date_received: limsFormData[idx]?.date_received || '',
        time_received: limsFormData[idx]?.time_received || '',
        dt_received_timezone: limsFormData[idx]?.dt_received_timezone || '',
      }));
 
      const unique_inventory_codes = tableBarCodeData.current?.data?.reduce((acc, curr) => {
        if (curr.inventory_code !== curr.old_inventory_code) {
          acc[curr.old_inventory_code] = curr.inventory_code;
        }
        return acc;
      }, {}) || {};
 
      const req_body = {
        tracking_details_data,
        unique_inventory_codes,
      };
 
      const resp = await ManagementService.saveAcsLimsTrackingNumbers(req_body);
 
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: resp.data,
        })
      );
 
      setShowLimsResponseModal(false);
      resetFormState();
    } catch (error) {
      setSubmitting(false);
      dispatch(
        snackbarActions.showNotification({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage:
            typeof error?.response?.data?.detail === 'string'
              ? error.response.data.detail
              : 'Something went wrong.',
        })
      );
    }
  };
 
  if (currentStep === 'upload') {
    return (
      <UIWrapper title="Manifest Upload New" paper>
        <Typography color="secondary" sx={{ fontSize: '17px', mb: 3 }}>
          Upload your manifest file (CSV or Excel) to review and edit all rows before ingestion.
        </Typography>
 
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              size="small"
              options={clientOptions || []}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
              isOptionEqualToValue={(option, value) => option === value || option?.name === value}
              value={clientState}
              onChange={(e, val) => {
                setClientState(val);
                setProjectState(null);
                setMappingState(null);
              }}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
          </Grid>
 
          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              size="small"
              disabled={!clientState}
              options={projectOptions || []}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
              isOptionEqualToValue={(option, value) => option === value || option?.name === value}
              value={projectState}
              onChange={(e, val) => {
                setProjectState(val);
              }}
              renderInput={(params) => <TextField {...params} label="Project" disabled={!clientState} />}
            />
          </Grid>
 
          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              size="small"
              disabled={!clientState}
              options={mappingOptions?.map(m => m?.name) || []}
              value={mappingState}
              onChange={(e, val) => {
                setMappingState(val);
              }}
              renderInput={(params) => <TextField {...params} label="Mapping Profile" disabled={!clientState} />}
            />
          </Grid>
        </Grid>
 
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
 
        <Box sx={{ maxWidth: 600 }}>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileSelect(e.dataTransfer.files);
            }}
            sx={{ border: '2px dashed #9b51e0', borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer', backgroundColor: '#f9f5ff' }}
          >
            <CloudUploadIcon sx={{ fontSize: 60, color: '#9b51e0', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>Drag and Drop Your File or</Typography>            
            <Button variant="contained" sx={{ backgroundColor: '#9b51e0' }} onClick={() => fileInputRef.current?.click()}>
              Choose File
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={(e) => handleFileSelect(e.target.files)} />
            <Typography variant="caption" display="block" sx={{ mt: 2, color: '#666', fontWeight: 'bold' }}>
              Supported files: CSV, XLS, XLSX
            </Typography>
          </Box>
 
          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 2, color: '#333' }}>
              File selected: <strong>{selectedFile.name}</strong>
            </Typography>
          )}
 
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || loading || !clientState || !projectState || !mappingState}
            sx={{ mt: 3, backgroundColor: '#9b51e0' }}
          >
            {loading ? 'Processing...' : 'Review Manifest'}
          </Button>
        </Box>
      </UIWrapper>
    );
  }
 
  return (
    <UIWrapper title="Review & Edit Manifest Data" paper>
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ mb: 1, color: '#333' }}>
          <strong>Client:</strong> {clientState} | <strong>Project:</strong> {projectState} | <strong>Profile:</strong> {mappingState}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
          Total Rows: <strong>{editedData.length}</strong> | Columns: <strong>{displayColumns.length}</strong>
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Click any field to edit inline. Changes are made directly in the cells below.
        </Typography>
      </Box>
 
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'auto',
          mb: 3,
          border: '1px solid #ddd'
        }}
      >
        <Table sx={{ minWidth: 1000 }} stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: '60px',
                  backgroundColor: '#9b51e0',
                  color: 'white',
                  textAlign: 'center'
                }}
              >
                #
              </TableCell>
              {displayColumns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 'bold',
                    minWidth: '150px',
                    backgroundColor: '#9b51e0',
                    color: 'white',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
 
          <TableBody>
            {editedData
              .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
              .map((row, displayIndex) => {
                const actualIndex = (currentPage - 1) * rowsPerPage + displayIndex;
                const serialNumber = actualIndex + 1;
                return (
                  <TableRow key={actualIndex} hover sx={{ '&:hover': { backgroundColor: '#f9f5ff' } }}>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        width: '60px',
                        textAlign: 'center',
                        p: 1
                      }}
                    >
                      {serialNumber}
                    </TableCell>
                    {displayColumns.map((col) => (
                      <TableCell key={`${actualIndex}-${col}`} sx={{ p: 0 }}>
                        <input
                          type="text"
                          maxLength="50"
                          value={row[col] || ''}
                          onChange={(e) => handleCellChange(actualIndex, col, e.target.value.slice(0, 50))}                          
                          style={{
                            width: '100%',
                            border: 'none',
                            padding: '8px',
                            fontFamily: 'inherit',
                            fontSize: '0.875rem',
                            boxSizing: 'border-box',
                            backgroundColor: 'transparent',
                            outline: 'none',
                          }}
                          onFocus={(e) => {
                            e.target.style.backgroundColor = '#fffbf0';
                            e.target.style.borderBottom = '2px solid #9b51e0';
                          }}
                          onBlur={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderBottom = 'none';
                          }}
                          onMouseEnter={(e) => {
                            if (e.target !== document.activeElement) {
                              e.target.style.backgroundColor = '#fff9e6';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (e.target !== document.activeElement) {
                              e.target.style.backgroundColor = 'transparent';
                            }
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
       
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3, flexDirection: 'column' }}>        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Showing {Math.min((currentPage - 1) * rowsPerPage + 1, editedData.length)} to {Math.min(currentPage * rowsPerPage, editedData.length)} of {editedData.length} rows
          </Typography>
          <Pagination
            count={Math.ceil(editedData.length / rowsPerPage)}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
            sx={{
              '& .MuiPaginationItem-page.Mui-selected': {
                backgroundColor: '#9b51e0',
                color: 'white'
              },
              '& .MuiPaginationItem-page:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          />
        </Box>
      </Box>
       
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setCurrentStep('upload');
            setEditedData([]);
            setSelectedFile(null);
            setCurrentPage(1);
            tableBarCodeData.current = null;
          }}
          disabled={submitting}
        >
          Back to Upload
        </Button>
 
        <Button
          variant="outlined"
          color="success"
          onClick={onExport}
          disabled={submitting || !mappingProfile}
        >
          Export Data
        </Button>
 
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirmationShow}
          disabled={submitting || loading}
          sx={{ backgroundColor: '#9b51e0' }}
        >
          {submitting || loading ? (loading ? 'Loading...' : 'Ingesting...') : 'Proceed to Save'}
        </Button>
 
        <Button
          variant="contained"
          color="primary"
          onClick={handleProceedToLims}
          disabled={submitting || loading}
          sx={{ backgroundColor: '#4CAF50' }}
        >
          {submitting || loading ? 'Processing...' : 'Proceed to LIMS'}
        </Button>
      </Box>
       
      <Dialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Ingest this manifest?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2, mb: 3, lineHeight: 1.6 }}>
            Confirming will accession these samples into SampledSphere and please verify that all of the information in the manifest is valid and correct.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmationDialog(false)}
            sx={{ color: '#666' }}
          >
            BACK
          </Button>
          <Button
            onClick={handleConfirmationConfirm}
            variant="contained"
            sx={{ backgroundColor: '#9b51e0' }}
          >
            CONFIRM
          </Button>
        </DialogActions>
      </Dialog>
       
      <Dialog open={showConflictDialog} onClose={() => setShowConflictDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>⚠️ Conflict Warning</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
            {conflictMessage}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={!preventFutureDate}
                onChange={(e) => setPreventFutureDate(!e.target.checked)}
              />
            }
            label="Allow future collection dates"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConflictDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConflictConfirm}
            variant="contained"
            color="error"
            sx={{ backgroundColor: '#d32f2f' }}
          >
            Proceed with Ingestion
          </Button>
        </DialogActions>
      </Dialog>
       
      {showUnique && selectedFile ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleCloseUnique}
        >
          <Box
            sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'auto', mt: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <AcsUniqueInvCode
              loading={loading}
              tableBarCodeData={tableBarCodeData.current}
              hamdleParentSubmission={handleUniqueCodesCallback}
              service_name="accession_manifest"
            />
          </Box>
        </div>
      ) : null}
       
      <Dialog open={showLimsResponseModal} onClose={() => setShowLimsResponseModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 600 }}>Send Samples To LIMS</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 3 }}>
            <TableContainer component={Paper} sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Tracking Number</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Manifest Count</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Carrier</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Date Received</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Time Received</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9b51e0', color: 'white' }}>Timezone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {limsResponseData
                    .slice(limsPageLarge * limsRowsPerPageLarge, limsPageLarge * limsRowsPerPageLarge + limsRowsPerPageLarge)
                    .map((row, idx) => (
                      <TableRow key={idx} hover sx={{ '&:hover': { backgroundColor: '#f9f5ff' } }}>
                        <TableCell>{row.client || '-'}</TableCell>
                        <TableCell>{row.project || '-'}</TableCell>
                        <TableCell>{row.tracking_number || '-'}</TableCell>
                        <TableCell>{row.accessioning_mainfest_count || '-'}</TableCell>
                        <TableCell>
                          <FormControl variant="standard" sx={{ minWidth: '150px' }} size="small" error={!limsFormData[idx]?.carrier}>
                            <InputLabel style={visuallyHidden} id={`carrier-label-${idx}`}>
                              Carrier
                            </InputLabel>
                            <Select
                              labelId={`carrier-label-${idx}`}
                              id={`carrier-${idx}`}
                              value={limsFormData[idx]?.carrier || ''}
                              onChange={(e) => handleLimsFormFieldChange(idx, 'carrier', e.target.value)}
                              aria-label="Carrier"
                              sx={{ marginTop: '0 !important' }}
                            >
                              {carrierTypes.map((option) => (
                                <MenuItem key={option.value} value={option.value || ''}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            id={`r_date_${idx}`}
                            label={<span style={{ display: 'none' }}>Date Received</span>}
                            type="date"
                            error={!limsFormData[idx]?.date_received}
                            variant="standard"
                            value={limsFormData[idx]?.date_received || ''}
                            onChange={(e) => handleLimsFormFieldChange(idx, 'date_received', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { marginTop: '0px' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            id={`r_time_${idx}`}
                            label={<span style={{ display: 'none' }}>Time Received</span>}
                            type="time"
                            variant="standard"
                            value={limsFormData[idx]?.time_received || ''}
                            error={!limsFormData[idx]?.time_received}
                            onChange={(e) => handleLimsFormFieldChange(idx, 'time_received', e.target.value)}
                            size="small"
                            sx={{ '& .MuiInputBase-root': { marginTop: '0px' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            id={`tags-tz_${idx}`}
                            freeSolo
                            value={limsFormData[idx]?.dt_received_timezone || ''}
                            options={timeZonesList}
                            onChange={(e, new_value) => handleLimsFormFieldChange(idx, 'dt_received_timezone', new_value)}
                            getOptionLabel={(option) => (typeof option === 'string' || option instanceof String ? option : '')}
                            style={{ minWidth: '140px' }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                error={!limsFormData[idx]?.dt_received_timezone?.trim()}
                                size="small"
                                type="text"
                                name="user_timezone"
                                label={<span style={visuallyHidden}>Timezone</span>}
                                sx={{
                                  '& .MuiFormLabel-asterisk': { display: 'none' },
                                  '& .MuiInputBase-root': { marginTop: '0px' },
                                }}
                              />
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
             
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Rows per page:
                <select
                  value={limsRowsPerPageLarge}
                  onChange={(e) => { setLimsRowsPerPageLarge(parseInt(e.target.value)); setLimsPageLarge(0); }}
                  style={{ marginLeft: '8px', padding: '4px' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Showing {Math.min(limsPageLarge * limsRowsPerPageLarge + 1, limsResponseData.length)} to {Math.min((limsPageLarge + 1) * limsRowsPerPageLarge, limsResponseData.length)} of {limsResponseData.length}
              </Typography>
              <Box>
                <Button
                  disabled={limsPageLarge === 0}
                  onClick={() => setLimsPageLarge(limsPageLarge - 1)}
                  sx={{ mr: 1 }}
                >
                  Previous
                </Button>
                <Button
                  disabled={(limsPageLarge + 1) * limsRowsPerPageLarge >= limsResponseData.length}
                  onClick={() => setLimsPageLarge(limsPageLarge + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setShowLimsResponseModal(false);
              resetFormState();
            }}
            sx={{ color: '#666' }}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#9b51e0' }}
            onClick={handleSendToLimsSubmit}
            disabled={submitting}
          >
            {submitting ? 'SENDING...' : 'SEND TO LIMS'}
          </Button>
        </DialogActions>
      </Dialog>
 
      <CSVLink
        ref={csvLinkRef}
        data={exportData}
        filename={`manifest_${clientState}_${projectState}_${new Date().getTime()}.csv`}
        style={{ display: 'none' }}
      />
    </UIWrapper>
  );
};
 
export default ManifestUploadNew;